import React from "react";
import * as Sentry from "@sentry/capacitor";

type State = { error: Error | null };

/**
 * Root-level error boundary. Catches any render or lifecycle error in the
 * component tree below it and shows a recovery screen instead of a blank
 * white page.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console so it shows up in browser DevTools / Vercel logs
    console.error("[ErrorBoundary] Caught render error:", error, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#0a0d12",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem",
            fontFamily: "system-ui, sans-serif",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>Something went wrong</div>
          <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.6)", maxWidth: 380 }}>
            The app ran into an unexpected error. Reloading usually fixes it.
          </div>
          <button
            onClick={() => window.location.replace("/")}
            style={{
              marginTop: "0.5rem",
              padding: "0.6rem 1.4rem",
              background: "rgba(99,102,241,0.85)",
              color: "white",
              border: "none",
              borderRadius: 10,
              fontSize: "0.9rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Reload app
          </button>
          {import.meta.env.DEV && (
            <pre
              style={{
                marginTop: "1rem",
                fontSize: "0.7rem",
                color: "rgba(255,255,255,0.4)",
                maxWidth: "90vw",
                overflow: "auto",
                textAlign: "left",
              }}
            >
              {this.state.error.message}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
